// =============================================================================
// MeuDriver — Booking Step 3: Confirm + Coupon + Payment
// =============================================================================

import { useEffect, useState } from 'react'
import { ChevronLeft, Check, Loader2, Tag, X, Gift, Wallet, Star, Package } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CancellationPolicy } from '@/components/shared/CancellationPolicy'
import { useBookingStore } from '@/store/bookingStore'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import {
  formatCurrency,
  getInitials,
  getInstructorPayout,
  getStudentPrice,
} from '@/lib/utils'
import {
  DURATION_OPTIONS,
  PREMIUM_DISCOUNT_PERCENT,
  TRIAL_DURATION_MINUTES,
  TRIAL_PRICE_MULTIPLIER,
} from '@/lib/constants'
import type { InstructorWithProfile } from '@/types/database'
import { useWalletBalance } from '@/hooks/useWallet'
import type { AppliedCoupon, AppliedGiftCard } from './BookingFlowShared'

function formatBookingDate(dateStr: string, timeStr: string): string {
  try {
    const d = new Date(`${dateStr}T${timeStr}:00`)
    const dayName = format(d, 'EEEE', { locale: ptBR })
    const dayCapitalized = dayName.charAt(0).toUpperCase() + dayName.slice(1)
    const dayNum = format(d, 'd', { locale: ptBR })
    const monthName = format(d, 'MMMM', { locale: ptBR })
    const time = format(d, "HH'h'mm", { locale: ptBR })
    return `${dayCapitalized}, ${dayNum} de ${monthName} às ${time}`
  } catch {
    return `${dateStr} ${timeStr}`
  }
}

interface Props {
  instructor: InstructorWithProfile
  onBack: () => void
  onConfirm: () => Promise<void>
  isSubmitting: boolean
  appliedCoupon: AppliedCoupon | null
  onApplyCoupon: (coupon: AppliedCoupon | null) => void
  appliedGiftCard?: AppliedGiftCard | null
  onApplyGiftCard?: (gc: AppliedGiftCard | null) => void
  useReferralCredit?: boolean
  onToggleReferralCredit?: (next: boolean) => void
  availableReferralCredits?: number
  // G2 — Loyalty points
  useLoyaltyPoints?: boolean
  onToggleLoyaltyPoints?: (next: boolean) => void
  availableLoyaltyPoints?: number
}

export function BookingStep3Confirm({
  instructor, onBack, onConfirm, isSubmitting, appliedCoupon, onApplyCoupon,
  appliedGiftCard = null, onApplyGiftCard,
  useReferralCredit = false, onToggleReferralCredit,
  availableReferralCredits = 0,
  useLoyaltyPoints = false, onToggleLoyaltyPoints,
  availableLoyaltyPoints = 0,
}: Props) {
  const store = useBookingStore()
  const { user, profile } = useAuth()
  const { data: walletBalance = 0 } = useWalletBalance()
  const [couponInput, setCouponInput] = useState(appliedCoupon?.code ?? '')
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState('')
  const [giftCardInput, setGiftCardInput] = useState(appliedGiftCard?.code ?? '')
  const [giftCardLoading, setGiftCardLoading] = useState(false)
  const [giftCardError, setGiftCardError] = useState('')

  const name = instructor.profile?.full_name ?? 'Instrutor'
  const hourlyRate = instructor.hourly_rate ?? 0
  const isTrial   = store.bookingMode === 'trial'
  // Aula de pacote: usa crédito já pago — sem cobrança, cupom ou descontos.
  const isPackage = store.bookingMode === 'package' && !!store.studentPackageId
  const effectiveDuration = isTrial ? TRIAL_DURATION_MINUTES : store.durationMinutes
  const unitPrice = hourlyRate * (effectiveDuration / 60) * (isTrial ? TRIAL_PRICE_MULTIPLIER : 1)
  // Valor do instrutor → preço ao aluno (+40 %). Ver getStudentPrice.
  const instructorBase = unitPrice
  const subtotal = getStudentPrice(instructorBase)
  const instructorPayout = getInstructorPayout(instructorBase)

  // Premium: 10 % antes do cupom (create-checkout aplica o mesmo no servidor).
  const premiumDiscount = profile?.is_premium
    ? Math.round(subtotal * PREMIUM_DISCOUNT_PERCENT) / 100
    : 0
  const subtotalAfterPremium = subtotal - premiumDiscount

  const couponDiscount = appliedCoupon
    ? Math.min(
        appliedCoupon.discount_type === 'percent'
          ? (subtotalAfterPremium * appliedCoupon.discount_value) / 100
          : appliedCoupon.discount_value,
        subtotalAfterPremium,
      )
    : 0

  // O subtotal já inclui a taxa da plataforma (+40 % sobre o instrutor).
  // Espelha `create-checkout`, que recalcula tudo no servidor.
  const totalBeforeCreditOrGc = Math.max(0, subtotalAfterPremium - couponDiscount)
  const giftCardDiscount = appliedGiftCard
    ? Math.min(appliedGiftCard.available_amount, totalBeforeCreditOrGc)
    : 0
  const totalAfterGc = Math.max(0, totalBeforeCreditOrGc - giftCardDiscount)
  const referralCreditApplied = useReferralCredit
    ? Math.min(availableReferralCredits, totalAfterGc)
    : 0
  const totalAfterReferral = Math.max(0, totalAfterGc - referralCreditApplied)

  // G2: pontos de fidelidade — 100pts = R$ 1
  const loyaltyPointsValue = useLoyaltyPoints
    ? Math.min(availableLoyaltyPoints / 100, totalAfterReferral)
    : 0

  const total = Math.max(0, totalAfterReferral - loyaltyPointsValue)
  // Tudo é pago com saldo; se faltar, o Pix cobra só a diferença (recarga).
  const pixDifference = Math.max(0, Math.round((total - walletBalance) * 100) / 100)
  const discount = couponDiscount + giftCardDiscount + referralCreditApplied + loyaltyPointsValue

  const dateStr = store.selectedDate ? format(store.selectedDate, 'yyyy-MM-dd') : ''
  const timeStr = store.selectedTime ?? ''
  const durationLabel =
    DURATION_OPTIONS.find((o) => o.value === store.durationMinutes)?.label ?? `${store.durationMinutes} min`

  async function handleApplyCoupon() {
    const code = couponInput.trim().toUpperCase()
    if (!code) return
    setCouponError('')
    setCouponLoading(true)
    try {
      // RPC SECURITY DEFINER `validate_coupon` — substitui o acesso direto
      // a `.from('coupons')` (que ficou bloqueado por RLS após D-01).
      const { data, error } = await supabase.rpc('validate_coupon', { p_code: code })

      const row = Array.isArray(data) ? data[0] : data
      if (error || !row) {
        setCouponError('Cupom inválido ou expirado.')
        onApplyCoupon(null)
        return
      }
      if (row.min_order != null && subtotal < Number(row.min_order)) {
        setCouponError(`Valor mínimo para este cupom: ${formatCurrency(Number(row.min_order))}.`)
        onApplyCoupon(null)
        return
      }
      onApplyCoupon({
        id: row.id,
        code: row.code,
        discount_type: row.discount_type,
        discount_value: Number(row.discount_value),
      })
      toast.success('Cupom aplicado com sucesso!')
    } finally {
      setCouponLoading(false)
    }
  }

  function handleRemoveCoupon() {
    onApplyCoupon(null)
    setCouponInput('')
    setCouponError('')
  }

  async function handleApplyGiftCard() {
    const code = giftCardInput.trim().toUpperCase()
    if (!code) return
    if (!onApplyGiftCard) return
    setGiftCardError('')
    setGiftCardLoading(true)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const jwt = sessionData.session?.access_token
      if (!jwt) {
        setGiftCardError('Faça login para usar um vale-aula.')
        return
      }

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/redeem-gift-card`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ code, subtotal: total > 0 ? total : subtotal }),
      })
      const result = await res.json()
      if (!res.ok) {
        const errMap: Record<string, string> = {
          not_found: 'Vale-aula não encontrado ou inativo.',
          expired: 'Este vale-aula expirou.',
          no_balance: 'Vale-aula sem saldo restante.',
          invalid_input: 'Código inválido.',
          unauthorized: 'Sessão expirada. Faça login novamente.',
        }
        setGiftCardError(errMap[result.error] ?? 'Erro ao validar vale-aula.')
        return
      }
      onApplyGiftCard({
        gift_card_id: result.gift_card_id,
        code,
        available_amount: Number(result.available_amount),
        remaining: Number(result.remaining),
        expires_at: result.expires_at,
      })
      toast.success(`Vale-aula aplicado: ${formatCurrency(Number(result.available_amount))}`)
    } catch (e) {
      setGiftCardError(e instanceof Error ? e.message : 'Erro inesperado.')
    } finally {
      setGiftCardLoading(false)
    }
  }

  function handleRemoveGiftCard() {
    onApplyGiftCard?.(null)
    setGiftCardInput('')
    setGiftCardError('')
  }

  // Garante que `user` é consultado (evita warning de variável não usada e
  // permite extensão futura para mostrar o e-mail do destinatário).
  useEffect(() => { void user }, [user])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Confirme os detalhes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="flex items-center gap-3 pb-3 border-b">
            <Avatar className="h-10 w-10">
              <AvatarImage src={instructor.profile?.avatar_url ?? undefined} />
              <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                {getInitials(name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-gray-900">{name}</p>
              <p className="text-muted-foreground text-xs">Instrutor</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Data e horário</span>
              <span className="font-medium text-right">
                {dateStr && timeStr ? formatBookingDate(dateStr, timeStr) : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Duração</span>
              <span className="font-medium">{durationLabel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Local de partida</span>
              <span className="font-medium text-right max-w-[55%]">
                {store.pickupAddress || '—'}
              </span>
            </div>
            {store.notes && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Observações</span>
                <span className="font-medium text-right max-w-[55%]">{store.notes}</span>
              </div>
            )}
          </div>

          {isPackage ? (
            <div className="border-t pt-3 space-y-1.5">
              <div className="flex justify-between font-bold text-base">
                <span className="flex items-center gap-1">
                  <Package className="h-4 w-4" />
                  1 aula do seu pacote
                </span>
                <span className="text-primary">Já paga</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Nenhuma cobrança agora: a aula usa 1 crédito do pacote.
              </p>
            </div>
          ) : (
          <div className="border-t pt-3 space-y-1.5">
            {isTrial ? (
              <div className="flex justify-between text-muted-foreground">
                <span>Aula experimental 30 min (50% off)</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
            ) : (
              <div className="flex justify-between text-muted-foreground">
                <span>Aula ({durationLabel})</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
            )}
            {premiumDiscount > 0 && (
              <div className="flex justify-between text-amber-700">
                <span className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5" />
                  Desconto Premium ({PREMIUM_DISCOUNT_PERCENT}%)
                </span>
                <span>−{formatCurrency(premiumDiscount)}</span>
              </div>
            )}
            {appliedCoupon && (
              <div className="flex justify-between text-green-600">
                <span className="flex items-center gap-1">
                  <Tag className="h-3.5 w-3.5" />
                  Desconto ({appliedCoupon.code})
                </span>
                <span>−{formatCurrency(couponDiscount)}</span>
              </div>
            )}
            {appliedGiftCard && giftCardDiscount > 0 && (
              <div className="flex justify-between text-purple-700">
                <span className="flex items-center gap-1">
                  <Gift className="h-3.5 w-3.5" />
                  Vale-aula ({appliedGiftCard.code})
                </span>
                <span>−{formatCurrency(giftCardDiscount)}</span>
              </div>
            )}
            {referralCreditApplied > 0 && (
              <div className="flex justify-between text-orange-700">
                <span className="flex items-center gap-1">
                  <Wallet className="h-3.5 w-3.5" />
                  Crédito de indicação
                </span>
                <span>−{formatCurrency(referralCreditApplied)}</span>
              </div>
            )}
            {loyaltyPointsValue > 0 && (
              <div className="flex justify-between text-amber-700">
                <span className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5" />
                  Pontos de fidelidade
                </span>
                <span>−{formatCurrency(loyaltyPointsValue)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base pt-1 border-t mt-1">
              <span>Total a pagar</span>
              <span className="text-primary">{formatCurrency(total)}</span>
            </div>
            <p className="text-xs text-muted-foreground pt-1">
              O instrutor receberá {formatCurrency(instructorPayout)} após a conclusão da aula.
            </p>
          </div>
          )}
        </CardContent>
      </Card>

      {!isPackage && (
      <>
      <div className="space-y-2">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <Tag className="h-4 w-4 text-primary" />
          Cupom de desconto
        </Label>
        {appliedCoupon ? (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
            <Check className="h-4 w-4 text-green-600 shrink-0" />
            <span className="text-sm font-medium text-green-800 flex-1">
              {appliedCoupon.code} — {appliedCoupon.discount_type === 'percent'
                ? `${appliedCoupon.discount_value}% de desconto`
                : `${formatCurrency(appliedCoupon.discount_value)} de desconto`}
            </span>
            <button onClick={handleRemoveCoupon} className="text-green-600 hover:text-green-800">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              placeholder="Ex: PRIMEIRAULA"
              value={couponInput}
              onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError('') }}
              className="h-10 uppercase"
              onKeyDown={(e) => { if (e.key === 'Enter') handleApplyCoupon() }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleApplyCoupon}
              disabled={couponLoading || !couponInput.trim()}
              className="shrink-0"
            >
              {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aplicar'}
            </Button>
          </div>
        )}
        {couponError && (
          <p className="text-xs text-red-600">{couponError}</p>
        )}
      </div>

      {/* Gift card */}
      {onApplyGiftCard && (
        <div className="space-y-2">
          <Label className="text-sm font-semibold flex items-center gap-2">
            <Gift className="h-4 w-4 text-purple-600" />
            Vale-aula
          </Label>
          {appliedGiftCard ? (
            <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2.5">
              <Check className="h-4 w-4 text-purple-600 shrink-0" />
              <span className="text-sm font-medium text-purple-800 flex-1">
                {appliedGiftCard.code} — {formatCurrency(appliedGiftCard.available_amount)} aplicados
                {appliedGiftCard.remaining > appliedGiftCard.available_amount && (
                  <span className="text-purple-600 ml-1">
                    (saldo após: {formatCurrency(appliedGiftCard.remaining - appliedGiftCard.available_amount)})
                  </span>
                )}
              </span>
              <button onClick={handleRemoveGiftCard} className="text-purple-600 hover:text-purple-800">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                placeholder="Ex: GC-XXXX-XXXX-XXXX"
                value={giftCardInput}
                onChange={(e) => { setGiftCardInput(e.target.value.toUpperCase()); setGiftCardError('') }}
                className="h-10 uppercase font-mono"
                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyGiftCard() }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleApplyGiftCard}
                disabled={giftCardLoading || !giftCardInput.trim()}
                className="shrink-0"
              >
                {giftCardLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aplicar'}
              </Button>
            </div>
          )}
          {giftCardError && <p className="text-xs text-red-600">{giftCardError}</p>}
        </div>
      )}

      {/* Crédito de indicação */}
      {onToggleReferralCredit && availableReferralCredits > 0 && (
        <div className="space-y-2 rounded-xl border border-orange-200 bg-orange-50 p-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={useReferralCredit}
              onChange={(e) => onToggleReferralCredit(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-orange-300 text-orange-600 focus:ring-orange-500"
            />
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-900 flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                Usar meu crédito de indicação
              </p>
              <p className="text-xs text-orange-700 mt-0.5">
                Disponível: <strong>{formatCurrency(availableReferralCredits)}</strong>. Aplica automaticamente até o valor desta aula.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Pontos de fidelidade */}
      {onToggleLoyaltyPoints && availableLoyaltyPoints >= 100 && (
        <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={useLoyaltyPoints}
              onChange={(e) => onToggleLoyaltyPoints(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
            />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                <Star className="h-4 w-4" />
                Usar meus pontos de fidelidade
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Disponível: <strong>{availableLoyaltyPoints.toLocaleString('pt-BR')} pts</strong> ({formatCurrency(availableLoyaltyPoints / 100)}).
                Aplica automaticamente até o valor desta aula.
              </p>
            </div>
          </label>
        </div>
      )}

      </>
      )}

      <CancellationPolicy compact />

      {!isPackage && total > 0 && (
      <Card className={pixDifference > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}>
        <CardContent className="p-4">
          {pixDifference > 0 ? (
            <>
              <p className="text-sm text-amber-800 font-medium mb-1">
                Seu saldo: {formatCurrency(walletBalance)} — faltam {formatCurrency(pixDifference)}
              </p>
              <p className="text-xs text-amber-700">
                Você paga a diferença via Pix. Ela entra como saldo e a aula é confirmada assim que o
                Pix cair. O valor da aula fica travado até ela acontecer.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-green-800 font-medium mb-1">
                Pago com seu saldo ({formatCurrency(walletBalance)} disponível)
              </p>
              <p className="text-xs text-green-700">
                O valor fica travado até a aula acontecer. Se cancelar com 2 horas ou mais de
                antecedência, ele volta para o seu saldo.
              </p>
            </>
          )}
        </CardContent>
      </Card>
      )}

      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onBack} disabled={isSubmitting} className="gap-1">
          <ChevronLeft className="h-4 w-4" />
          Voltar
        </Button>
        <Button
          size="lg"
          onClick={onConfirm}
          disabled={isSubmitting}
          className="flex-1 sm:flex-none"
        >
          {isSubmitting
            ? 'Aguarde...'
            : isPackage || total <= 0
            ? 'Confirmar agendamento'
            : pixDifference > 0
            ? `Confirmar e pagar ${formatCurrency(pixDifference)} via Pix`
            : 'Confirmar e pagar com saldo'}
        </Button>
      </div>
    </div>
  )
}
